import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArchiveRestore, CheckCircle2, Circle, Copy, ExternalLink, EyeOff, Globe, Plus, Save, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useStaff } from "@/context/StaffAuth";
import { api, errCode, errMsg } from "@/lib/api";
import { dateTime, titleCase } from "@/lib/format";
import { ConfirmDialog, DemoBadge, ErrorState, Field, KeyValue, PageHeader, SectionCard, StatusBadge } from "@/components/admin/AdminUI";
import { ProductVariants } from "@/components/admin/ProductVariants";
import { ProductImages } from "@/components/admin/ProductImages";

const SIZE_GUIDES = [["none", "No size guide"], ["women_rtw", "Women ready-to-wear"], ["men_rtw", "Men ready-to-wear"], ["men_tailoring", "Men tailoring"], ["kids_rtw", "Kids ready-to-wear"], ["women_shoes", "Women shoes"], ["men_shoes", "Men shoes"], ["kids_shoes", "Kids shoes"], ["accessories", "Accessories"]];

const AUDIENCES = [["women", "Women"], ["men", "Men"], ["kids", "Kids"], ["unisex", "Unisex"]];
const blank = { name: "", slug: "", brand_id: "", category_id: "", gender: "women", collection_ids: [], description: "", materials: "", composition: "", care: "", made_in: "", attributes: [], tags: "", size_guide: "none", seo_title: "", seo_description: "", publish_start: "", publish_end: "" };

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocal = (s) => (s ? new Date(s).toISOString() : null);

const toForm = (p) => ({
  name: p.name || "", slug: p.slug || "", brand_id: p.brand_id || "", category_id: p.category_id || "", gender: p.gender || "unisex", collection_ids: p.collection_ids || [],
  description: p.description || "", materials: (p.materials || []).join(", "), composition: p.composition || "", care: p.care || "", made_in: p.made_in || "",
  attributes: (p.attributes || []).map((a) => ({ ...a })), tags: (p.tags || []).join(", "), size_guide: p.size_guide || "none",
  seo_title: p.seo_title || "", seo_description: p.seo_description || "", publish_start: toLocal(p.publish_start), publish_end: toLocal(p.publish_end),
});

const serialize = (f, force = false) => ({
  name: f.name.trim(), slug: f.slug.trim() || null, brand_id: f.brand_id, category_id: f.category_id, gender: f.gender, collection_ids: f.collection_ids,
  description: f.description, materials: f.materials.split(",").map((s) => s.trim()).filter(Boolean), composition: f.composition, care: f.care, made_in: f.made_in,
  attributes: f.attributes.filter((a) => a.name.trim() && a.value.trim()), tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean), size_guide: f.size_guide,
  seo_title: f.seo_title, seo_description: f.seo_description, publish_start: fromLocal(f.publish_start), publish_end: fromLocal(f.publish_end), force,
});

const publishIssues = (p) => {
  if (!p?.id) return ["Save the product"];
  const issues = [];
  if (!(p.description || "").trim()) issues.push("Add a product description");
  if (!(p.variants || []).some((v) => v.active)) issues.push("Add at least one active variant with a price");
  if (!(p.images || []).length) issues.push("Add at least one product image");
  if (!p.brand || p.brand.status !== "active") issues.push("Brand is archived or missing");
  if (!p.category || p.category.status !== "active") issues.push("Category is archived or missing");
  if (p.status === "archived") issues.push("Restore the product before publishing");
  return issues;
};

export default function AdminProductEdit() {
  const { pid } = useParams();
  const isNew = !pid;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can } = useStaff();
  const canWrite = can("catalog.write");
  const canPublish = can("catalog.publish");
  const canPrice = can("catalog.pricing");

  const [product, setProduct] = useState(null);
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [refs, setRefs] = useState({ brands: [], categories: [], collections: [] });
  const [history, setHistory] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type}
  const [dupe, setDupe] = useState(null);
  const tab = params.get("tab") || "details";
  const setTab = (t) => { const n = new URLSearchParams(params); n.set("tab", t); setParams(n, { replace: true }); };

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/products/${pid}`);
      setProduct(r.data);
      setForm(toForm(r.data));
      setDirty(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [pid, isNew]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/brands", { params: { page_size: 500, status: "" } }),
      api.get("/admin/categories", { params: { page_size: 500, status: "" } }),
      api.get("/admin/collections", { params: { page_size: 500, status: "" } }),
    ]).then(([b, c, col]) => setRefs({ brands: b.data.items || [], categories: c.data.items || [], collections: col.data.items || [] })).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "history" && pid) api.get(`/admin/products/${pid}/history`).then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
  }, [tab, pid, product]);

  useEffect(() => {
    document.title = `${isNew ? "New product" : product?.name || "Product"} · Admin`;
  }, [isNew, product]);

  const update = (k, v) => { setForm((s) => ({ ...s, [k]: v })); setDirty(true); };
  const onInput = (k) => (e) => update(k, e.target.value);

  const groupedCategories = useMemo(() => {
    const byGender = {};
    refs.categories.forEach((c) => { (byGender[c.gender] = byGender[c.gender] || []).push(c); });
    Object.values(byGender).forEach((arr) => arr.sort((a, b) => (a.parent_name || a.name).localeCompare(b.parent_name || b.name) || (a.parent_id ? 1 : -1)));
    return byGender;
  }, [refs.categories]);

  const save = async (force = false) => {
    if (!form.name.trim() || !form.brand_id || !form.category_id) {
      toast.error("Name, brand and category are required");
      setTab("details");
      return;
    }
    setBusy(true);
    try {
      const body = serialize(form, force);
      if (isNew) {
        const r = await api.post("/admin/products", body);
        toast.success("Product created. Add variants and imagery, then publish.");
        navigate(`/admin/products/${r.data.id}?tab=variants`, { replace: true });
      } else {
        const r = await api.put(`/admin/products/${pid}`, body);
        setProduct(r.data);
        setForm(toForm(r.data));
        setDirty(false);
        toast.success("Product saved");
      }
    } catch (err) {
      if (errCode(err) === "POSSIBLE_DUPLICATE") setDupe(err.response?.data?.error?.details?.existing || {});
      else toast.error(errMsg(err, "Could not save product"));
    } finally {
      setBusy(false);
    }
  };

  const transition = async (action) => {
    setBusy(true);
    try {
      if (action === "delete") {
        await api.delete(`/admin/products/${pid}`);
        toast.success("Draft deleted");
        navigate("/admin/products", { replace: true });
        return;
      }
      if (action === "duplicate") {
        const r = await api.post(`/admin/products/${pid}/duplicate`);
        toast.success("Product duplicated as a draft with inactive variants");
        navigate(`/admin/products/${r.data.id}`);
        return;
      }
      const r = await api.post(`/admin/products/${pid}/${action}`);
      setProduct(r.data);
      setForm(toForm(r.data));
      toast.success({ publish: "Product published to the storefront", unpublish: "Product unpublished", archive: "Product archived", restore: "Product restored as a draft" }[action]);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (loading && !product) return <div className="space-y-6"><Skeleton className="h-12 w-80" /><Skeleton className="h-10 w-full max-w-lg" /><Skeleton className="h-96 w-full" /></div>;

  const issues = publishIssues(product);
  const status = product?.status || "draft";

  return (
    <div data-testid="admin-product-edit-page">
      <PageHeader
        eyebrow={isNew ? "Catalog · New product" : `Catalog · ${product?.brand_name || product?.brand?.name || ""}`}
        title={isNew ? "New product" : product?.name}
        backTo="/admin/products"
        backLabel="All products"
        description={!isNew && (
          <span className="inline-flex flex-wrap items-center gap-2">
            <StatusBadge status={status} testId="product-status-badge" />
            {product?.is_demo && <DemoBadge />}
            <span className="text-xs text-muted-foreground">Slug <code>{product?.slug}</code> · updated {dateTime(product?.updated_at)}</span>
          </span>
        )}
        actions={(
          <>
            {!isNew && status === "published" && <Button asChild variant="ghost" size="sm"><a href={`/product/${product.slug}`} target="_blank" rel="noreferrer" data-testid="product-view-storefront-link"><ExternalLink className="h-4 w-4 mr-1.5" />View live</a></Button>}
            {!isNew && canWrite && <Button variant="outline" size="sm" onClick={() => setConfirm({ type: "duplicate" })} className="rounded-lg" data-testid="product-duplicate-button"><Copy className="h-4 w-4 mr-1.5" />Duplicate</Button>}
            {!isNew && canPublish && status !== "archived" && <Button variant="outline" size="sm" onClick={() => setConfirm({ type: "archive" })} className="rounded-lg" data-testid="admin-archive-button"><Archive className="h-4 w-4 mr-1.5" />Archive</Button>}
            {!isNew && canPublish && status === "archived" && <Button variant="outline" size="sm" onClick={() => transition("restore")} className="rounded-lg" data-testid="product-restore-button"><ArchiveRestore className="h-4 w-4 mr-1.5" />Restore</Button>}
            {!isNew && canPublish && status === "published" && <Button variant="outline" size="sm" onClick={() => setConfirm({ type: "unpublish" })} className="rounded-lg" data-testid="product-unpublish-button"><EyeOff className="h-4 w-4 mr-1.5" />Unpublish</Button>}
            {!isNew && canPublish && status === "draft" && <Button size="sm" variant="secondary" onClick={() => transition("publish")} disabled={busy || issues.length > 0 || dirty} title={issues.join("; ")} className="rounded-lg" data-testid="admin-publish-button"><Globe className="h-4 w-4 mr-1.5" />Publish</Button>}
            {canWrite && <Button size="sm" onClick={() => save(false)} disabled={busy || (!isNew && !dirty)} className="rounded-lg" data-testid="admin-save-button"><Save className="h-4 w-4 mr-1.5" />{busy ? "Saving…" : isNew ? "Create product" : "Save"}</Button>}
          </>
        )}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-lg h-10 bg-secondary" data-testid="product-editor-tabs">
          <TabsTrigger value="details" data-testid="product-tab-details">Details</TabsTrigger>
          <TabsTrigger value="variants" data-testid="product-tab-variants">Variants{product && <span className="ml-1.5 text-[10px] text-muted-foreground tabular">{product.variants?.length || 0}</span>}</TabsTrigger>
          <TabsTrigger value="images" data-testid="product-tab-images">Images{product && <span className="ml-1.5 text-[10px] text-muted-foreground tabular">{product.images?.length || 0}</span>}</TabsTrigger>
          <TabsTrigger value="publishing" data-testid="product-tab-publishing">SEO & publishing</TabsTrigger>
          {!isNew && <TabsTrigger value="history" data-testid="product-tab-history">History</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <SectionCard title="Essentials" description="What the product is and where it lives in the taxonomy.">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Product name" htmlFor="p-name" required className="sm:col-span-2"><Input id="p-name" value={form.name} onChange={onInput("name")} disabled={!canWrite} placeholder="Double-breasted wool trench" data-testid="product-name-input" /></Field>
                  <Field label="Brand" htmlFor="p-brand" required>
                    <Select value={form.brand_id} onValueChange={(v) => update("brand_id", v)} disabled={!canWrite}>
                      <SelectTrigger id="p-brand" data-testid="product-brand-select"><SelectValue placeholder="Select brand" /></SelectTrigger>
                      <SelectContent>{refs.brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.status !== "active" ? " (archived)" : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Audience" htmlFor="p-aud" required hint="Who the piece is for — drives the Women / Men / Kids navigation">
                    <Select value={form.gender} onValueChange={(v) => update("gender", v)} disabled={!canWrite}>
                      <SelectTrigger id="p-aud" data-testid="product-audience-select"><SelectValue placeholder="Select audience" /></SelectTrigger>
                      <SelectContent>{AUDIENCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Category" htmlFor="p-cat" required hint="Department (Clothing / Shoes / Bags & Accessories) is inherited from the category">
                    <Select value={form.category_id} onValueChange={(v) => update("category_id", v)} disabled={!canWrite}>
                      <SelectTrigger id="p-cat" data-testid="product-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(groupedCategories).map(([g, arr]) => (
                          <SelectGroup key={g}>
                            <SelectLabel>{titleCase(g)}</SelectLabel>
                            {arr.map((c) => <SelectItem key={c.id} value={c.id}>{c.parent_name ? `${c.parent_name} / ` : ""}{c.name}{c.status !== "active" ? " (archived)" : ""}</SelectItem>)}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Slug" htmlFor="p-slug" hint="Leave empty to generate from brand + name" className="sm:col-span-2"><Input id="p-slug" value={form.slug} onChange={onInput("slug")} disabled={!canWrite} className="font-mono text-xs" data-testid="product-slug-input" /></Field>
                  <Field label="Description" htmlFor="p-desc" required className="sm:col-span-2" hint="Editorial, 2–4 sentences. Required for publishing."><Textarea id="p-desc" rows={5} value={form.description} onChange={onInput("description")} disabled={!canWrite} data-testid="product-description-input" /></Field>
                </div>
              </SectionCard>

              <SectionCard title="Composition & care" description="Shown on the product page below the description.">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Materials" htmlFor="p-mat" hint="Comma separated; used for filtering" className="sm:col-span-2"><Input id="p-mat" value={form.materials} onChange={onInput("materials")} disabled={!canWrite} placeholder="Wool, Cashmere" data-testid="product-materials-input" /></Field>
                  <Field label="Composition" htmlFor="p-comp"><Input id="p-comp" value={form.composition} onChange={onInput("composition")} disabled={!canWrite} placeholder="80% wool, 20% cashmere" /></Field>
                  <Field label="Made in" htmlFor="p-made"><Input id="p-made" value={form.made_in} onChange={onInput("made_in")} disabled={!canWrite} placeholder="Italy" /></Field>
                  <Field label="Care" htmlFor="p-care" className="sm:col-span-2"><Textarea id="p-care" rows={2} value={form.care} onChange={onInput("care")} disabled={!canWrite} placeholder="Dry clean only" /></Field>
                  <Field label="Size guide" htmlFor="p-sg">
                    <Select value={form.size_guide} onValueChange={(v) => update("size_guide", v)} disabled={!canWrite}>
                      <SelectTrigger id="p-sg" data-testid="product-sizeguide-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{SIZE_GUIDES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Tags" htmlFor="p-tags" hint="Comma separated, internal + search"><Input id="p-tags" value={form.tags} onChange={onInput("tags")} disabled={!canWrite} placeholder="SS26, runway" /></Field>
                </div>
              </SectionCard>

              <SectionCard title="Attributes" description="Free-form specifications (fit, length, closure…)." actions={canWrite && <Button variant="outline" size="sm" onClick={() => update("attributes", [...form.attributes, { name: "", value: "" }])} data-testid="product-add-attribute-button"><Plus className="h-4 w-4 mr-1" />Add</Button>}>
                {form.attributes.length === 0 ? <p className="text-sm text-muted-foreground">No attributes.</p> : (
                  <ul className="space-y-2">
                    {form.attributes.map((a, i) => (
                      <li key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                        <Input value={a.name} onChange={(e) => update("attributes", form.attributes.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Fit" disabled={!canWrite} aria-label="Attribute name" data-testid="product-attribute-name-input" />
                        <Input value={a.value} onChange={(e) => update("attributes", form.attributes.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} placeholder="Relaxed" disabled={!canWrite} aria-label="Attribute value" data-testid="product-attribute-value-input" />
                        <Button variant="ghost" size="icon" onClick={() => update("attributes", form.attributes.filter((_, j) => j !== i))} disabled={!canWrite} aria-label="Remove attribute"><X className="h-4 w-4" /></Button>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>

            <div className="space-y-6">
              <SectionCard title="Collections" description="Curated edits this product belongs to.">
                {refs.collections.length === 0 ? <p className="text-sm text-muted-foreground">No collections yet. <Link to="/admin/collections" className="underline underline-offset-4">Create one</Link>.</p> : (
                  <ul className="space-y-2.5">
                    {refs.collections.map((c) => {
                      const checked = form.collection_ids.includes(c.id);
                      return (
                        <li key={c.id} className="flex items-center gap-3">
                          <Checkbox id={`col-${c.id}`} checked={checked} disabled={!canWrite} onCheckedChange={(v) => update("collection_ids", v ? [...form.collection_ids, c.id] : form.collection_ids.filter((x) => x !== c.id))} data-testid="product-collection-checkbox" />
                          <label htmlFor={`col-${c.id}`} className="text-sm leading-none cursor-pointer">{c.name}{c.status !== "active" && <span className="text-muted-foreground"> (archived)</span>}</label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </SectionCard>
              {!isNew && (
                <SectionCard title="Snapshot">
                  <KeyValue items={[["Audience", titleCase(product?.gender)], ["Department", product?.category?.parent_name || product?.category?.name || "—"], ["Variants", `${product?.variants?.length || 0} (${(product?.variants || []).filter((v) => v.active).length} active)`], ["Available units", product?.available_total ?? 0], ["Images", product?.images?.length || 0], ["Created", dateTime(product?.created_at)], ["Published", product?.published_at ? dateTime(product.published_at) : "Never"]]} />
                </SectionCard>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="variants" className="mt-6">
          <ProductVariants product={product} onChange={load} canWrite={canWrite} canPrice={canPrice} />
        </TabsContent>

        <TabsContent value="images" className="mt-6">
          <ProductImages product={product} onChange={load} canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="publishing" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <SectionCard title="Search engine listing" description="Falls back to brand + name when empty.">
                <div className="space-y-4">
                  <Field label="SEO title" htmlFor="p-seo-t" hint={`${form.seo_title.length}/70`}><Input id="p-seo-t" maxLength={70} value={form.seo_title} onChange={onInput("seo_title")} disabled={!canWrite} data-testid="product-seo-title-input" /></Field>
                  <Field label="SEO description" htmlFor="p-seo-d" hint={`${form.seo_description.length}/170`}><Textarea id="p-seo-d" rows={3} maxLength={170} value={form.seo_description} onChange={onInput("seo_description")} disabled={!canWrite} data-testid="product-seo-description-input" /></Field>
                  <div className="rounded-lg border bg-secondary/50 p-4">
                    <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-1">Preview</p>
                    <p className="text-sm font-medium text-[hsl(var(--accent-forest))] truncate">{form.seo_title || `${refs.brands.find((b) => b.id === form.brand_id)?.name || ""} ${form.name}`.trim() || "Product title"}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{form.seo_description || form.description?.slice(0, 160) || "Product description…"}</p>
                  </div>
                </div>
              </SectionCard>
              <SectionCard title="Scheduled visibility" description="Optional window in which a published product is visible. Times are stored in UTC.">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Visible from" htmlFor="p-ps"><Input id="p-ps" type="datetime-local" value={form.publish_start} onChange={onInput("publish_start")} disabled={!canWrite} data-testid="product-publish-start-input" /></Field>
                  <Field label="Visible until" htmlFor="p-pe"><Input id="p-pe" type="datetime-local" value={form.publish_end} onChange={onInput("publish_end")} disabled={!canWrite} data-testid="product-publish-end-input" /></Field>
                </div>
              </SectionCard>
            </div>
            <div className="space-y-6">
              <SectionCard title="Publish readiness" description={issues.length ? "Resolve these before publishing." : "Everything required is in place."} testId="product-publish-checklist">
                <ul className="space-y-2.5 text-sm">
                  {["Add a product description", "Add at least one active variant with a price", "Add at least one product image", "Brand is archived or missing", "Category is archived or missing"].map((label) => {
                    const bad = issues.includes(label) || !product?.id;
                    const ok = !bad;
                    const pretty = { "Brand is archived or missing": "Brand is active", "Category is archived or missing": "Category is active" }[label] || label.replace("Add a", "Has a").replace("Add at least one", "Has at least one");
                    return (
                      <li key={label} className="flex items-start gap-2.5">
                        {ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-[hsl(var(--accent-forest))] shrink-0" /> : <Circle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />}
                        <span className={ok ? "" : "text-muted-foreground"}>{pretty}</span>
                      </li>
                    );
                  })}
                </ul>
                {dirty && <p className="text-xs text-[hsl(var(--accent-ochre))] mt-4">Save your changes before publishing.</p>}
              </SectionCard>
              {!isNew && (
                <SectionCard title="Lifecycle">
                  <KeyValue items={[["Status", <StatusBadge status={status} />], ["First published", product?.published_at ? dateTime(product.published_at) : "—"], ["Last published", product?.last_published_at ? dateTime(product.last_published_at) : "—"], ["Unpublished", product?.unpublished_at ? dateTime(product.unpublished_at) : "—"], ["Archived", product?.archived_at ? dateTime(product.archived_at) : "—"], ["Stock history", product?.has_stock_history ? "Yes — cannot be deleted" : "None"]]} />
                  {canPublish && status === "draft" && !product?.published_at && !product?.has_stock_history && (
                    <Button variant="outline" size="sm" className="mt-5 text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive" onClick={() => setConfirm({ type: "delete" })} data-testid="product-delete-button"><Trash2 className="h-4 w-4 mr-1.5" />Delete draft</Button>
                  )}
                </SectionCard>
              )}
            </div>
          </div>
        </TabsContent>

        {!isNew && (
          <TabsContent value="history" className="mt-6">
            <SectionCard title="Audit history" description="Every privileged change to this product, its variants and images." testId="product-history">
              {history === null ? <Skeleton className="h-32 w-full" /> : history.length === 0 ? <p className="text-sm text-muted-foreground">No history yet.</p> : (
                <ol className="relative border-l ml-2 space-y-6">
                  {history.map((h) => (
                    <li key={h.id} className="pl-6 relative" data-testid="product-history-item">
                      <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-foreground" />
                      <p className="text-sm"><span className="font-semibold">{h.action}</span>{h.summary && <span className="text-muted-foreground"> · {h.summary}</span>}</p>
                      <p className="text-xs text-muted-foreground">{h.actor_email} · {dateTime(h.created_at)}</p>
                      {h.changes && Object.keys(h.changes).length > 0 && (
                        <details className="mt-2 text-xs">
                          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">{Object.keys(h.changes).length} field{Object.keys(h.changes).length > 1 ? "s" : ""} changed</summary>
                          <ul className="mt-2 space-y-1 rounded-lg bg-secondary/60 p-3 font-mono">
                            {Object.entries(h.changes).map(([k, v]) => <li key={k} className="break-all"><span className="text-muted-foreground">{k}:</span> {JSON.stringify(v.from)} → {JSON.stringify(v.to)}</li>)}
                          </ul>
                        </details>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </SectionCard>
          </TabsContent>
        )}
      </Tabs>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={{ archive: "Archive this product?", unpublish: "Unpublish this product?", delete: "Delete this draft permanently?", duplicate: "Duplicate this product?" }[confirm?.type] || ""}
        description={{ archive: "Archived products disappear from the storefront and pickers. Stock history is preserved and the product can be restored.", unpublish: "The product returns to draft and is hidden from customers immediately.", delete: "Only never-published drafts without stock or purchasing history can be deleted. Retired SKUs can never be reused.", duplicate: "Creates a new draft with copied details, images and inactive variants with new SKUs." }[confirm?.type] || ""}
        confirmLabel={{ archive: "Archive", unpublish: "Unpublish", delete: "Delete", duplicate: "Duplicate" }[confirm?.type] || "Confirm"}
        destructive={confirm?.type === "delete"}
        busy={busy}
        onConfirm={() => transition(confirm.type)}
      />

      <ConfirmDialog
        open={!!dupe}
        onOpenChange={(o) => !o && setDupe(null)}
        title="Possible duplicate"
        description={`A product named "${dupe?.name || form.name}" already exists for this brand (${dupe?.status || "unknown"}). Create anyway?`}
        confirmLabel="Create anyway"
        busy={busy}
        onConfirm={() => { setDupe(null); save(true); }}
        testId="product-duplicate-warning-dialog"
      />
    </div>
  );
}
